import { Client, Room } from 'colyseus.js';
import { ServerEvent, GameMode } from '../../shared/types';
import { FREE_ROOM_NAME, BR_ROOM_NAME } from '../../shared/constants';
import type { PlayerCommand } from '../../shared/types';
import { PingMonitor } from './PingMonitor';

export type StateChangeCallback = (state: any) => void;
export type EventCallback = (eventType: string, data: any) => void;

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';
export type ConnectionStateListener = (state: ConnectionState) => void;
export type PingListener = (ms: number) => void;

const RECONNECT_MAX_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 1000;

export class ClientNetManager {
    private client: Client | null = null;
    private room: Room | null = null;
    private stateCallbacks: StateChangeCallback[] = [];
    private eventCallbacks: EventCallback[] = [];
    private pingMonitor: PingMonitor | null = null;
    private connectionState: ConnectionState = 'connecting';
    private connectionListeners: ConnectionStateListener[] = [];
    private pingListeners: PingListener[] = [];
    private pingUnsubscribe: (() => void) | null = null;
    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectionToken: string | null = null;

    async join(
        serverUrl: string,
        username: string,
        options?: { admin?: boolean; mode?: GameMode; loadout?: number[]; enemy?: string },
    ): Promise<Room> {
        this.client = new Client(serverUrl);
        this.setConnectionState('connecting');
        const roomName = options?.mode === GameMode.Br ? BR_ROOM_NAME : FREE_ROOM_NAME;
        this.room = await this.client.joinOrCreate(roomName, { username, ...options });
        this.attachRoomLifecycle(this.room);
        this.startPingMonitor(this.room);
        this.setConnectionState('open');

        this.attachRoomMessageHandlers(this.room);

        this.room.onMessage('error', (data) => {
            console.error('Server error:', data.message);
        });

        this.emitCurrentStateToAll();
        for (const delayMs of [0, 100, 500, 1000, 2000]) {
            setTimeout(() => this.emitCurrentStateToAll(), delayMs);
        }

        return this.room;
    }

    sendCommand(command: PlayerCommand): void {
        this.room?.send('command', command);
    }

    onStateChange(callback: StateChangeCallback): void {
        this.stateCallbacks.push(callback);
        this.emitCurrentState(callback);
    }

    onServerEvent(callback: EventCallback): void {
        this.eventCallbacks.push(callback);
    }

    getSessionId(): string {
        return this.room?.sessionId ?? '';
    }

    getRoom(): Room | null {
        return this.room;
    }

    disconnect(): void {
        this.cancelReconnectTimer();
        this.stopPingMonitor();
        this.room?.leave();
        this.room = null;
        this.client = null;
        this.stateCallbacks = [];
        this.eventCallbacks = [];
        this.setConnectionState('closed');
        this.connectionListeners = [];
        this.pingListeners = [];
    }

    private emitCurrentState(callback: StateChangeCallback): void {
        if (this.room?.state) {
            callback(this.room.state);
        }
    }

    private emitCurrentStateToAll(): void {
        if (!this.room?.state) return;
        for (const cb of this.stateCallbacks) {
            cb(this.room.state);
        }
    }

    /** Bind state-change + server-event listeners to a freshly-joined room.
     * Shared by the initial join and the reconnect path. */
    private attachRoomMessageHandlers(room: Room): void {
        room.onStateChange((state) => {
            for (const cb of this.stateCallbacks) cb(state);
        });
        for (const event of Object.values(ServerEvent)) {
            room.onMessage(event, (data) => {
                for (const cb of this.eventCallbacks) cb(event, data);
            });
        }
    }

    /** Subscribe to connection-state changes. Fires immediately with the
     * current value so UI components render correctly on mount. */
    onConnectionState(listener: ConnectionStateListener): () => void {
        this.connectionListeners.push(listener);
        listener(this.connectionState);
        return () => {
            const idx = this.connectionListeners.indexOf(listener);
            if (idx >= 0) this.connectionListeners.splice(idx, 1);
        };
    }

    /** Subscribe to ping (RTT) updates. Fires immediately with the latest
     * sample (0 until the first pong arrives). */
    onPing(listener: PingListener): () => void {
        this.pingListeners.push(listener);
        listener(this.pingMonitor?.currentPingMs ?? 0);
        return () => {
            const idx = this.pingListeners.indexOf(listener);
            if (idx >= 0) this.pingListeners.splice(idx, 1);
        };
    }

    getConnectionState(): ConnectionState {
        return this.connectionState;
    }

    getCurrentPingMs(): number {
        return this.pingMonitor?.currentPingMs ?? 0;
    }

    /** Manual reconnect using the most recent reconnection token. Used by
     * the auto-reconnect loop and exposed for UI buttons. */
    async reconnect(): Promise<Room | null> {
        if (!this.client || !this.reconnectionToken) return null;
        this.setConnectionState('reconnecting');
        try {
            const room = await this.client.reconnect(this.reconnectionToken);
            this.room = room;
            this.attachRoomLifecycle(room);
            this.startPingMonitor(room);
            this.attachRoomMessageHandlers(room);
            this.setConnectionState('open');
            this.reconnectAttempts = 0;
            this.emitCurrentStateToAll();
            return room;
        } catch (err) {
            console.warn('reconnect failed', err);
            return null;
        }
    }

    private setConnectionState(state: ConnectionState): void {
        if (this.connectionState === state) return;
        this.connectionState = state;
        for (const listener of this.connectionListeners) listener(state);
    }

    private attachRoomLifecycle(room: Room): void {
        // Cache the token immediately so a sudden socket close still leaves
        // us with a value to feed back into client.reconnect().
        this.reconnectionToken = room.reconnectionToken;

        room.onError((code, message) => {
            console.error('room error', code, message);
        });

        room.onLeave((code) => {
            this.stopPingMonitor();
            // Code 1000 (normal close) means the player or server initiated
            // a clean leave — no recovery needed.
            if (code === 1000 || this.connectionState === 'closed') {
                this.setConnectionState('closed');
                return;
            }
            this.setConnectionState('reconnecting');
            this.scheduleReconnect();
        });
    }

    private scheduleReconnect(): void {
        if (this.reconnectTimer !== null) return;
        if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
            this.setConnectionState('closed');
            return;
        }
        const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts);
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            const room = await this.reconnect();
            if (room) return;
            if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
                this.setConnectionState('closed');
            } else {
                this.scheduleReconnect();
            }
        }, delay);
    }

    private cancelReconnectTimer(): void {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.reconnectAttempts = 0;
    }

    private startPingMonitor(room: Room): void {
        this.stopPingMonitor();
        this.pingMonitor = new PingMonitor({
            send: (type, payload) => room.send(type, payload),
            onMessage: (type, callback) => room.onMessage(type, callback as (msg: unknown) => void),
        });
        this.pingUnsubscribe = this.pingMonitor.subscribe((ms) => {
            for (const l of this.pingListeners) l(ms);
        });
        this.pingMonitor.start();
    }

    private stopPingMonitor(): void {
        if (this.pingUnsubscribe) {
            this.pingUnsubscribe();
            this.pingUnsubscribe = null;
        }
        if (this.pingMonitor) {
            this.pingMonitor.stop();
            this.pingMonitor = null;
        }
    }
}
